import BillingContactsPicker from '../components/BillingContactsPicker';
import { buildBillingContacts, readBillingContacts } from '../utils/billingContacts';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import { listPlants } from '../services/plants';
import { listParts } from '../services/parts';
import { listSuppliers } from '../services/suppliers';
import {
  getInvoice,
  updateInvoice,
  deleteInvoice,
  INVOICE_STATUSES,
} from '../services/invoices';
import { formatCurrency } from '../utils/formatters';
import { exportInvoicePdf } from '../utils/pdfExport';

/**
 * InvoiceDetail — editable view of a single saved invoice. Reached by
 * clicking an invoice number on the Sent Invoices or Invoice Tracker
 * tabs. Director-only (route-guarded in App.jsx AND render-guarded
 * here, matching the Invoices index page).
 *
 * Everything on the record is editable:
 *   • Header fields — status, issue / due dates, billing period,
 *     PO number, payment terms, bill schedule, notes.
 *   • Customer block — billing address text and the up-to-three
 *     contact names (dropdowns when the supplier record has contacts
 *     on file; free-text inputs otherwise).
 *   • Line items — description, details, plant, dates, quantity,
 *     rate, and amount on every line, plus add / remove. Amount
 *     auto-recomputes as quantity × rate while a rate is present;
 *     clear the rate to type a flat amount directly.
 *
 * The invoice Total is always the live sum of line amounts — it
 * recalculates as lines change and is stamped onto the record on
 * Save. plantCodes are likewise re-derived from the edited lines so
 * the Sent Invoices plant grouping stays accurate.
 *
 * Save persists via updateInvoice. IMPORTANT: the service's encode()
 * step JSON-stringifies plantCodes / lineItems / timesheetIds /
 * adjustmentIds / contactNames from whatever model it's handed — an
 * omitted array would be saved as []. So the save payload always
 * carries ALL five arrays, with timesheetIds / adjustmentIds passed
 * through untouched from the loaded record (they link the invoice to
 * its source Billing Tracker entries and must survive edits).
 *
 * Download PDF renders the current edited state (not the last-saved
 * state) through the same exportInvoicePdf the draft page uses, so
 * what you see in the form is exactly what lands in the PDF.
 */
export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDirector } = useAuth();

  // ---- Loaded data --------------------------------------------------
  const [invoice, setInvoice] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- Editable state ------------------------------------------------
  // Initialized from the loaded record in the effect below. Kept as
  // individual fields (not one big object) so each input wires up the
  // same way the draft page's fields do.
  const [status, setStatus] = useState('Sent');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [billSchedule, setBillSchedule] = useState('');
  const [notes, setNotes] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  // Main + Additional Billing Contacts (Oct 2026).
  const [mainContactName, setMainContactName] = useState('');
  const [additionalContactNames, setAdditionalContactNames] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  // Monotonic counter for keys on lines added in this session.
  const [newLineSeq, setNewLineSeq] = useState(1);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  // ---- Delete (confirmation modal) ----------------------------------
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Permanently remove this invoice, then leave the page (the record no
  // longer exists). The Billing Tracker entries it referenced are NOT
  // touched — there's no "invoiced" flag on them, so dropping the
  // invoice automatically frees those timesheets to be billed again.
  const onDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteInvoice(id);
      navigate('/invoices/sent', { replace: true });
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete this invoice.');
      setDeleting(false);
    }
  }, [id, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inv, supRows, plantRows, partRows] = await Promise.all([
        getInvoice(id),
        listSuppliers(),
        listPlants(),
        listParts(),
      ]);
      setInvoice(inv);
      setSuppliers(supRows || []);
      setPlants(plantRows || []);
      setParts(partRows || []);
      // Seed the editable fields from the record.
      setStatus(inv?.Status || 'Sent');
      setIssueDate((inv?.IssueDate || '').slice(0, 10));
      setDueDate((inv?.DueDate || '').slice(0, 10));
      setPeriodStart((inv?.PeriodStart || '').slice(0, 10));
      setPeriodEnd((inv?.PeriodEnd || '').slice(0, 10));
      setPoNumber(inv?.PoNumber || '');
      setPaymentTerms(inv?.PaymentTerms || '');
      setBillSchedule(inv?.BillSchedule || '');
      setNotes(inv?.Notes || '');
      setBillingAddress(inv?.BillingAddress || '');
      // Older invoices only stored names: the first becomes the main
      // contact, the rest Additional Billing Contacts.
      const picked = readBillingContacts(inv);
      setMainContactName(picked.mainName);
      setAdditionalContactNames(picked.additionalNames);
      // Stamp a stable _key on every line so React list rendering and
      // the per-line update/remove handlers can target rows even
      // after add/remove reshuffles indexes.
      setLineItems(
        (Array.isArray(inv?.lineItems) ? inv.lineItems : []).map(
          (li, idx) => ({ ...li, _key: `saved-${idx}` }),
        ),
      );
      setDirty(false);
      setSavedAt(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Lookups -------------------------------------------------------
  const plantByCode = useMemo(() => {
    const map = new Map();
    for (const p of plants || []) {
      if (p.PlantCode) map.set(String(p.PlantCode).toUpperCase(), p);
    }
    return map;
  }, [plants]);

  const supplier = useMemo(() => {
    const sc = String(invoice?.SupplierCode || '').toUpperCase();
    if (!sc) return null;
    return (
      (suppliers || []).find(
        (s) => String(s.SupplierCode || '').toUpperCase() === sc,
      ) || null
    );
  }, [invoice, suppliers]);

  // ---- Derived -------------------------------------------------------
  // Live total — always the sum of the edited line amounts.
  const total = useMemo(
    () => lineItems.reduce((acc, li) => acc + (Number(li.amount) || 0), 0),
    [lineItems],
  );

  // Plant codes re-derived from the edited lines ('—' bucket and
  // blanks excluded) — saved onto the record so Sent Invoices keeps
  // grouping this invoice under the right plants.
  const plantCodesOnInvoice = useMemo(() => {
    const set = new Set();
    for (const li of lineItems) {
      const code = String(li.plantCode || '').trim().toUpperCase();
      if (code && code !== '—') set.add(code);
    }
    return Array.from(set);
  }, [lineItems]);

  const isCreditMemo = String(invoice?.InvoiceNumber || '').startsWith('CR-');

  // Plant code → distinct part numbers this invoice's supplier ships
  // to that plant. Used to back-fill `partNumberLabel` on line items
  // when downloading the PDF — invoices generated before June 2026
  // didn't stamp it at creation time.
  const partNumbersByPlant = useMemo(() => {
    const sc = String(invoice?.SupplierCode || '').toUpperCase();
    const map = new Map();
    if (!sc) return map;
    for (const p of parts || []) {
      if (String(p.SupplierCode || '').toUpperCase() !== sc) continue;
      const num = String(p.PartNumber || '').trim();
      if (!num) continue;
      for (const code of p.plantCodes || []) {
        const key = String(code || '').toUpperCase();
        if (!key) continue;
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(num);
      }
    }
    const out = new Map();
    for (const [key, set] of map) out.set(key, Array.from(set).join(', '));
    return out;
  }, [parts, invoice]);

  // ---- Line item handlers ---------------------------------------------
  const markDirty = () => {
    setDirty(true);
    setSavedAt(null);
  };

  const updateLine = (key, patch) => {
    markDirty();
    setLineItems((prev) =>
      prev.map((li) => {
        if (li._key !== key) return li;
        const next = { ...li, ...patch };
        // Amount follows quantity × rate while a rate is present.
        // When the rate is blank/null the amount input is free-typed,
        // so we leave whatever the user entered alone.
        const rateNum = Number(next.rate);
        const qtyNum = Number(next.quantity);
        if (
          next.rate !== '' &&
          next.rate != null &&
          Number.isFinite(rateNum) &&
          Number.isFinite(qtyNum) &&
          ('quantity' in patch || 'rate' in patch)
        ) {
          next.amount = qtyNum * rateNum;
        }
        return next;
      }),
    );
  };

  const removeLine = (key) => {
    markDirty();
    setLineItems((prev) => prev.filter((li) => li._key !== key));
  };

  const addLine = () => {
    markDirty();
    const idx = newLineSeq;
    setNewLineSeq((n) => n + 1);
    setLineItems((prev) => [
      ...prev,
      {
        _key: `new-${idx}`,
        category: 'Manual',
        plantCode: '',
        plantTitle: '',
        weekStart: '',
        dateRangeStart: '',
        dateRangeEnd: '',
        description: '',
        details: '',
        quantity: '',
        unitLabel: '',
        rate: null,
        amount: 0,
        submitterName: '',
      },
    ]);
  };

  // ---- Build the record from the edited state -------------------------
  // Shared by Save and Download PDF so both always operate on the
  // exact same shape.
  const buildEditedRecord = () => {
    const cleanedLines = lineItems.map(({ _key, ...li }) => {
      const plantCode = String(li.plantCode || '').trim().toUpperCase();
      return {
        ...li,
        quantity:
          li.quantity === '' || li.quantity == null
            ? li.quantity
            : Number(li.quantity),
        rate: li.rate === '' || li.rate == null ? null : Number(li.rate),
        amount: Number(li.amount) || 0,
        plantCode,
        plantTitle: plantByCode.get(plantCode)?.Title || li.plantTitle || '',
        // Back-fill the part number for invoices saved before it was
        // stamped at generation time; keep whatever's already there.
        partNumberLabel:
          li.partNumberLabel || partNumbersByPlant.get(plantCode) || '',
      };
    });
    const cleanedContacts = String(mainContactName || '').trim()
      ? [String(mainContactName).trim()]
      : [];
    const billingContacts = buildBillingContacts(
      supplier,
      mainContactName,
      additionalContactNames,
      invoice?.billingContacts,
    );
    return {
      ...invoice,
      Status: status,
      IssueDate: issueDate,
      DueDate: dueDate,
      PeriodStart: periodStart,
      PeriodEnd: periodEnd,
      PoNumber: poNumber,
      PaymentTerms: paymentTerms,
      BillSchedule: billSchedule,
      Notes: notes,
      BillingAddress: billingAddress,
      Total: cleanedLines.reduce(
        (acc, li) => acc + (Number(li.amount) || 0),
        0,
      ),
      lineItems: cleanedLines,
      contactNames: cleanedContacts,
      billingContacts,
      plantCodes: (() => {
        const set = new Set();
        for (const li of cleanedLines) {
          if (li.plantCode && li.plantCode !== '—') set.add(li.plantCode);
        }
        return Array.from(set);
      })(),
      // Pass-throughs — encode() stringifies these from the model, so
      // they must ride along on every save or they'd be wiped to [].
      timesheetIds: invoice?.timesheetIds || [],
      adjustmentIds: invoice?.adjustmentIds || [],
    };
  };

  const onSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const record = buildEditedRecord();
      // Strip the page-side id and decoded helper arrays the encode()
      // step doesn't expect as columns; updateInvoice handles the
      // arrays itself via encode().
      const { id: _id, ...model } = record;
      const saved = await updateInvoice(invoice.id, model);
      setInvoice(saved);
      setDirty(false);
      setSavedAt(new Date());
    } catch (err) {
      console.error('[invoice detail] save failed', err);
      setSaveError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const onDownloadPdf = () => {
    const record = buildEditedRecord();
    exportInvoicePdf({
      invoice: record,
      periodLabel: formatPeriodLabel(periodStart, periodEnd),
      customer: supplier,
      lineItems: record.lineItems,
      total: record.Total,
      isCreditMemo,
    });
  };

  // ---- Render guards --------------------------------------------------
  if (!isDirector) {
    return (
      <div className="card">
        <div className="card-title">Invoice</div>
        <p className="muted">Only Directors can view and edit invoices.</p>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load Invoice</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  if (!invoice) {
    return (
      <div className="card">
        <div className="card-title">Invoice Not Found</div>
        <p className="muted">
          No invoice matches this link.{' '}
          <Link to="/invoices">Back to Invoices</Link>.
        </p>
      </div>
    );
  }

  // Group the edited lines by plant for display — same visual rhythm
  // as the draft preview ('—'/blank bucket renders last under
  // "Adjustments").
  const linesByPlant = (() => {
    const map = new Map();
    for (const li of lineItems) {
      const key =
        li.section === 'onCallFee'
          ? 'onCallFee'
          : String(li.plantCode || '').trim().toUpperCase() || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(li);
    }
    const rank = (k) => (k === '—' ? 2 : k === 'onCallFee' ? 1 : 0);
    return Array.from(map.entries()).sort((a, b) => {
      const ra = rank(a[0]);
      const rb = rank(b[0]);
      if (ra !== rb) return ra - rb;
      return String(a[0]).localeCompare(String(b[0]));
    });
  })();

  return (
    <>
      <div className="page-header">
        <h1>
          {isCreditMemo ? 'Credit Memo' : 'Invoice'}{' '}
          {invoice.InvoiceNumber || invoice.id}
        </h1>
        <div className="actions">
          <button
            className="btn btn-outline"
            onClick={() => navigate('/invoices')}
            disabled={saving}
          >
            ← Back to Invoices
          </button>
          <button
            className="btn btn-outline"
            onClick={onDownloadPdf}
            disabled={saving}
          >
            Download PDF
          </button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={saving || deleting}
          >
            Delete
          </button>
        </div>
      </div>

      <Modal
        open={confirmDeleteOpen}
        title={`Delete ${isCreditMemo ? 'credit memo' : 'invoice'} ${
          invoice.InvoiceNumber || invoice.id
        }?`}
        onClose={() => {
          if (!deleting) setConfirmDeleteOpen(false);
        }}
        closeLabel="Cancel"
        footer={
          <button className="btn btn-danger" onClick={onDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </button>
        }
      >
        <p style={{ margin: 0 }}>
          This permanently removes this{' '}
          {isCreditMemo ? 'credit memo' : 'invoice'} from the portal. The
          Billing Tracker entries it pulled from are <strong>not</strong>{' '}
          deleted — they simply become available to invoice again.
        </p>
        {deleteError && (
          <div className="error" style={{ marginTop: 12 }}>
            {deleteError}
          </div>
        )}
      </Modal>

      {saveError && (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            borderRadius: 6,
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid var(--brand-danger)',
            color: 'var(--brand-danger)',
            fontSize: 14,
          }}
        >
          {saveError}
        </div>
      )}
      {savedAt && !dirty && (
        <div
          role="status"
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            borderRadius: 6,
            background: 'rgba(22, 163, 74, 0.08)',
            border: '1px solid var(--brand-success, #16a34a)',
            color: 'var(--brand-success, #16a34a)',
            fontSize: 14,
          }}
        >
          Changes saved.
        </div>
      )}

      {/* ---------- Invoice Details ---------- */}
      <div className="card">
        <div className="card-title">Invoice Details</div>
        <div className="form-grid">
          <div className="field">
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                markDirty();
              }}
            >
              {INVOICE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Issue Date</label>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => {
                setIssueDate(e.target.value);
                markDirty();
              }}
            />
          </div>
          <div className="field">
            <label>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                markDirty();
              }}
            />
          </div>
          <div className="field">
            <label>Period Start</label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => {
                setPeriodStart(e.target.value);
                markDirty();
              }}
            />
          </div>
          <div className="field">
            <label>Period End</label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => {
                setPeriodEnd(e.target.value);
                markDirty();
              }}
            />
          </div>
          <div className="field">
            <label>PO Number</label>
            <input
              type="text"
              value={poNumber}
              onChange={(e) => {
                setPoNumber(e.target.value);
                markDirty();
              }}
            />
          </div>
          <div className="field">
            <label>Payment Terms</label>
            <input
              type="text"
              value={paymentTerms}
              onChange={(e) => {
                setPaymentTerms(e.target.value);
                markDirty();
              }}
              placeholder="e.g. Net 30"
            />
          </div>
          <div className="field">
            <label>Bill Schedule</label>
            <input
              type="text"
              value={billSchedule}
              onChange={(e) => {
                setBillSchedule(e.target.value);
                markDirty();
              }}
              placeholder="e.g. Monthly"
            />
          </div>
          <div className="field full">
            <label>Notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                markDirty();
              }}
            />
          </div>
        </div>
      </div>

      {/* ---------- Customer ---------- */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Customer</div>
        <div className="form-grid">
          <div className="field">
            <label>Customer</label>
            <div
              style={{
                padding: '8px 10px',
                background: '#fafafa',
                border: '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              {invoice.CustomerName || invoice.SupplierName || '—'}
            </div>
          </div>
          <div className="field">
            <label>Account Number</label>
            <div
              style={{
                padding: '8px 10px',
                background: '#fafafa',
                border: '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              {invoice.SupplierCode || '—'}
            </div>
          </div>
          <div className="field full">
            <label>Billing Address</label>
            <textarea
              rows={3}
              value={billingAddress}
              onChange={(e) => {
                setBillingAddress(e.target.value);
                markDirty();
              }}
              placeholder="Street&#10;City, State / Province, Zip / Postal Code&#10;Country"
            />
          </div>
          {/* Main Billing Contact (printed on the invoice) and
              Additional Billing Contacts (emailed a copy; not printed). */}
          <BillingContactsPicker
            contacts={supplier?.contacts}
            mainName={mainContactName}
            additionalNames={additionalContactNames}
            onChange={({ mainName, additionalNames }) => {
              setMainContactName(mainName);
              setAdditionalContactNames(additionalNames);
              markDirty();
            }}
          />
        </div>
      </div>

      {/* ---------- Line Items ---------- */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">Line Items</div>
        {lineItems.length === 0 ? (
          <p className="muted" style={{ margin: '0 0 12px' }}>
            No line items on this invoice. Add one below.
          </p>
        ) : (
          <table className="mini-table">
            <thead>
              <tr>
                <th style={{ minWidth: 110 }}>Plant</th>
                <th style={{ minWidth: 160 }}>Description</th>
                <th style={{ minWidth: 180 }}>Details</th>
                <th>Liaison</th>
                <th style={{ minWidth: 200 }}>Dates</th>
                <th style={{ textAlign: 'right' }}>Quantity</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {linesByPlant.map(([plant, plantLines]) => (
                <Fragment key={plant}>
                  <tr style={{ background: 'var(--brand-primary-soft, #fff4e6)' }}>
                    <td colSpan={9} style={{ padding: '6px 10px' }}>
                      {plant === 'onCallFee' ? (
                        <strong style={{ color: 'var(--brand-muted)' }}>
                          On-Call Engagement Fees
                        </strong>
                      ) : plant === '—' ? (
                        <strong style={{ color: 'var(--brand-muted)' }}>
                          Adjustments
                        </strong>
                      ) : (
                        <strong
                          style={{ color: 'var(--brand-primary-dark, #b14a14)' }}
                        >
                          {plantByCode.get(plant)?.Title || plant}
                        </strong>
                      )}
                    </td>
                  </tr>
                  {plantLines.map((li) => (
                    <EditableLineRow
                      key={li._key}
                      li={li}
                      plants={plants}
                      onChange={(patch) => updateLine(li._key, patch)}
                      onRemove={() => removeLine(li._key)}
                    />
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ textAlign: 'right', fontWeight: 600 }}>
                  {isCreditMemo ? 'Total Credit Amount' : 'Total Balance Due'}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {formatCurrency(total)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={addLine}>
            + Add Line Item
          </button>
        </div>
      </div>
    </>
  );
}

// ---- Editable line row -------------------------------------------------

const cellInputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--brand-border)',
  borderRadius: 4,
  fontSize: 13,
  background: '#fff',
  boxSizing: 'border-box',
};

function EditableLineRow({ li, plants, onChange, onRemove }) {
  return (
    <tr>
      <td>
        <select
          value={String(li.plantCode || '').toUpperCase()}
          onChange={(e) => onChange({ plantCode: e.target.value })}
          style={cellInputStyle}
        >
          <option value="">— None —</option>
          {(plants || [])
            .filter((p) => p.PlantCode)
            .slice()
            .sort((a, b) =>
              String(a.PlantCode).localeCompare(String(b.PlantCode)),
            )
            .map((p) => (
              <option key={p.PlantCode} value={String(p.PlantCode).toUpperCase()}>
                {p.PlantCode}
              </option>
            ))}
        </select>
      </td>
      <td>
        <input
          type="text"
          value={li.description || ''}
          onChange={(e) => onChange({ description: e.target.value })}
          style={cellInputStyle}
        />
      </td>
      <td>
        <input
          type="text"
          value={li.details || ''}
          onChange={(e) => onChange({ details: e.target.value })}
          placeholder="Notes (optional)"
          style={cellInputStyle}
        />
      </td>
      <td>
        <input
          type="text"
          value={li.submitterName || ''}
          onChange={(e) => onChange({ submitterName: e.target.value })}
          placeholder="—"
          style={{ ...cellInputStyle, minWidth: 90 }}
        />
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="date"
            value={(li.dateRangeStart || '').slice(0, 10)}
            onChange={(e) => onChange({ dateRangeStart: e.target.value })}
            style={cellInputStyle}
            title="Start of the date range shown on the invoice"
          />
          <input
            type="date"
            value={(li.dateRangeEnd || '').slice(0, 10)}
            onChange={(e) => onChange({ dateRangeEnd: e.target.value })}
            style={cellInputStyle}
            title="End of the date range shown on the invoice"
          />
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <input
            type="number"
            value={li.quantity ?? ''}
            onChange={(e) => onChange({ quantity: e.target.value })}
            style={{ ...cellInputStyle, width: 70, textAlign: 'right' }}
            step="0.25"
          />
          <input
            type="text"
            value={li.unitLabel || ''}
            onChange={(e) => onChange({ unitLabel: e.target.value })}
            placeholder="hrs"
            style={{ ...cellInputStyle, width: 48 }}
            title="Unit label (hrs / mi / blank)"
          />
        </div>
      </td>
      <td>
        <input
          type="number"
          value={li.rate ?? ''}
          onChange={(e) =>
            onChange({ rate: e.target.value === '' ? null : e.target.value })
          }
          placeholder="—"
          style={{ ...cellInputStyle, width: 80, textAlign: 'right' }}
          step="0.01"
          title="Leave blank to type the amount directly"
        />
      </td>
      <td>
        <input
          type="number"
          value={li.amount ?? ''}
          onChange={(e) => onChange({ amount: e.target.value })}
          disabled={li.rate !== '' && li.rate != null}
          style={{
            ...cellInputStyle,
            width: 100,
            textAlign: 'right',
            background:
              li.rate !== '' && li.rate != null ? '#fafafa' : '#fff',
          }}
          step="0.01"
          title={
            li.rate !== '' && li.rate != null
              ? 'Computed from quantity × rate — clear the rate to edit directly'
              : 'Flat amount'
          }
        />
      </td>
      <td style={{ textAlign: 'right' }}>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={onRemove}
          title="Remove this line"
          style={{ color: 'var(--brand-danger)' }}
        >
          ✕
        </button>
      </td>
    </tr>
  );
}

// ---- Helpers -------------------------------------------------------------

// "Apr 1, 2026 – Apr 30, 2026" — same shape the draft page stamps on
// generated PDFs.
function formatPeriodLabel(startIso, endIso) {
  const fmt = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  const s = fmt(startIso);
  const e = fmt(endIso);
  if (s && e) return `${s} – ${e}`;
  return s || e || '';
}
